import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "@/helpers/axiosBaseQuery";
import api from "@/store/api/api";
import { desksApi } from "@/store/api/v1/desks";

export const bookingsApi = createApi({
  reducerPath: "bookingsApi",
  tagTypes: ["bookings", "simpleBookings", "bookingSettings"],
  baseQuery: axiosBaseQuery({
    baseUrl: api.root,
  }),
  endpoints: (build) => ({
    getBookings: build.query({
      query: (data) => ({
        url: api.bookings.getBookings(),
        method: "GET",
        params: data,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return [endpointName, queryArgs?.location_id, queryArgs.date];
      },
      merge: (currentCache, newItems, { arg }) => {
        if (!arg.skip) {
          return newItems;
        }
        currentCache.push(
          ...newItems.filter(
            (newItem) => !currentCache.some((item) => item.id === newItem.id)
          )
        );
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        return currentArg !== previousArg;
      },
      providesTags: ["bookings"],
    }),
    getSimpleBookings: build.query({
      query: (data) => ({
        url: api.bookings.getSimpleBookings(),
        method: "GET",
        params: data,
      }),
      providesTags: ["simpleBookings"],
    }),
    deleteBooking: build.mutation({
      query: ({ bookingId, ...data }) => ({
        url: api.bookings.deleteBooking(bookingId),
        method: "DELETE",
        params: data
      }),
      invalidatesTags: ["simpleBookings"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(desksApi.util.invalidateTags(["desks", "desk-transactions"]));
        } catch {}
      },
    }),
    exportBookings: build.query({
      query: (data) => ({
        url: api.bookings.exportBookings(),
        method: "GET",
        params: data,
      }),
    }),
    createBooking: build.mutation({
      query: (data) => ({
        url: api.bookings.createBooking(),
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["simpleBookings"],
    }),
    updateBooking: build.mutation({
      query: ({ id, ...data }) => ({
        url: api.bookings.updateBooking(id),
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["simpleBookings"],
    }),
    changeDuration: build.mutation({
      query: (data) => ({
        url: api.bookings.changeDuration(),
        method: "PUT",
        body: data,
      }),
    }),
    updateStatus: build.mutation({
      query: ({ id, ...data }) => ({
        url: api.bookings.updateStatus(id),
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["bookings"],
    }),
    payBooking: build.mutation({
      query: ({ id, ...data }) => ({
        url: api.bookings.payBooking(id),
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["bookings"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(desksApi.util.invalidateTags(["desks", "desk-transactions"]));
        } catch {}
      },
    }),
    getBookingHistory: build.query({
      query: ({ id, ...data }) => ({
        url: api.bookings.getBookingHistory(id),
        method: "GET",
        params: data,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return [endpointName, queryArgs?.location_id]
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.skip === 0) {
          return newItems;
        }
        currentCache.push(...newItems.filter((newItem) => !currentCache.some((item) => item.id === newItem.id)))
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        return currentArg !== previousArg;
      },
    }),
    getWaitlistEntries: build.query({
      query: ({ id, ...data }) => ({
        url: api.bookings.getWaitlistEntries(id),
        method: "GET",
        params: data,
      }),
      providesTags: ["bookings"],
    }),
  }),
});

export const {
  useLazyGetBookingsQuery,
  useLazyGetSimpleBookingsQuery,
  useCreateBookingMutation,
  useChangeDurationMutation,
  useUpdateStatusMutation,
  usePayBookingMutation,
  useDeleteBookingMutation,
  useLazyExportBookingsQuery,
  useGetWaitlistEntriesQuery,
} = bookingsApi;
